import { gql } from "@apollo/client";
import {
    createPagePagingActions,
    SortDirection,
    TableFilterFinalForm,
    TableQuery,
    useTableQuery,
    useTableQueryFilter,
    useTableQueryPaging,
    useTableQuerySort,
} from "@comet/admin";
import { type ITableRowProps, Table, TableColumns, VisibleType } from "@comet/admin/lib/table/Table";
import { Refresh } from "@mui/icons-material";
import { Button, Dialog, Grid, IconButton } from "@mui/material";
import { ContentWrapper, formatDateTimeAsString, FormattedDateWidthDateTime, useMemoizedTableVariables } from "@src/components";
import { StaticSelectField } from "@src/components/fields/StaticSelect";
import { StyledFilter, TableFilter } from "@src/components/table/TableStyles";
import { Type } from "@src/components/Type";
import { type GQLCameraAdminQuery, type GQLLogsAdminQuery, type GQLLogsAdminQueryVariables } from "@src/graphql.generated";
import ArrowRight from "@src/icons/ArrowRight";
import { type ExportVariables, loadLimit } from "@src/utils";
import { CSVExportButton } from "@src/utils/CSVExportButton";
import { useCSVExportPagedTableQuery } from "@src/utils/usePagedCSVExport";
import { type FC, type PropsWithChildren, useCallback, useState } from "react";
import { FormattedMessage, type IntlShape, useIntl } from "react-intl";

import { ImageLogTable } from "../imageLog/ImageLog";
import { LastReceivedImageInformation } from "../LastImageInformation";
import SMSCodeForm from "../SMSCodeForm";
import { StyledChip, StyledCommandTypography, StyledContentTypography, StyledTableBodyRow, StyledTypography } from "./ActivityInformation.sc";
import { generateContent, generateType } from "./activityLog";
import ActivityInformationDetails from "./ActvitiyInformationDetails";

const generateLogMessageFilterOptions = (intl: IntlShape) => {
    return ["ReceivedSMS", "SendSMS", "FailedSMS", "CommandInvalid", "SendFailure", "VideoRequested", "VideoRequestFailed", "VideoReceived"].map(
        (message) => ({
            label: generateType(message, intl),
            value: message,
        }),
    );
};

const logsQuery = gql`
    query LogsAdmin($cameraId: ID!, $page: Int, $size: Int, $sort: SortInput, $message: String) {
        logsAdmin(cameraId: $cameraId, page: $page, size: $size, sort: $sort, message: $message) {
            nodes {
                id
                send
                receive
                createdAt
                message
                executedBy
                groupIdentifier
            }
            totalCount
            totalPages
            nextPage
            previousPage
        }
    }
`;

const ActivityInformation: FC<PropsWithChildren<GQLCameraAdminQuery & { refetchCamera: () => void }>> = ({ cameraAdmin, refetchCamera }) => {
    const intl = useIntl();
    const pagingApi = useTableQueryPaging(1);
    const filterApi = useTableQueryFilter<GQLLogsAdminQueryVariables>({ cameraId: cameraAdmin.id }, { pagingApi });
    const sortApi = useTableQuerySort({ columnName: "createdAt", direction: SortDirection.DESC });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentGroupID, setCurrentGroupId] = useState<null | string>(null);

    const { changePage } = pagingApi;
    const {
        current: { message },
    } = filterApi;
    const addAdditionalQueryVariablesFn = useCallback(() => ({ cameraId: cameraAdmin.id, size: 7, message }), [cameraAdmin.id, message]);

    const memoizedVariables = useMemoizedTableVariables<GQLLogsAdminQueryVariables>({ pagingApi, sortApi, addAdditionalQueryVariablesFn });

    const { tableData, loading, error, api, refetch } = useTableQuery<GQLLogsAdminQuery, GQLLogsAdminQueryVariables>()(logsQuery, {
        resolveTableData: ({ logsAdmin }) => ({
            data: logsAdmin.nodes,
            totalCount: logsAdmin.totalCount,
            pagingInfo: createPagePagingActions(pagingApi, logsAdmin),
        }),
        variables: memoizedVariables,
    });

    function handleDialogClose() {
        setDialogOpen(false);
        setCurrentGroupId("");
    }

    const fileName = intl.formatMessage(
        { id: "reviermanager.common.export.activity", defaultMessage: "Protokoll_Gerät_{id}" },
        { id: cameraAdmin.id },
    );

    const exportApi = useCSVExportPagedTableQuery<ExportVariables>(
        api,
        {
            fromPage: 1,
            toPage: tableData?.totalCount ? Math.ceil(tableData.totalCount / loadLimit) : 1,
            variablesForPage: (page) => {
                return {
                    ...memoizedVariables,
                    page,
                    size: loadLimit,
                };
            },
        },
        fileName,
    );

    const resetTable = useCallback(() => {
        changePage(1, 1, { noScrollToTop: true });
        refetch({ ...memoizedVariables, page: 1 });
        refetchCamera();
    }, [refetch, changePage, memoizedVariables, refetchCamera]);

    return (
        <>
            <Grid size={12}>
                <SMSCodeForm cameraAdmin={cameraAdmin} resetTable={resetTable} />
            </Grid>
            <Grid size={12}>
                <Dialog open={dialogOpen} onClose={handleDialogClose}>
                    {currentGroupID && <ActivityInformationDetails groupIdentifier={currentGroupID} />}
                </Dialog>

                <ContentWrapper
                    title={intl.formatMessage({ id: "reviermanager.camera.activity", defaultMessage: "Protokoll" })}
                    link={
                        <Button color="primary" variant="text" onClick={resetTable} startIcon={<Refresh />}>
                            <FormattedMessage id="reviermanager.camera.activity.refetch" defaultMessage="Aktualisieren" />
                        </Button>
                    }
                >
                    <TableFilterFinalForm filterApi={filterApi}>
                        <TableFilter container>
                            <Grid size={5}>
                                <StyledFilter>
                                    <StaticSelectField
                                        name="message"
                                        isClearable
                                        variant="vertical"
                                        options={generateLogMessageFilterOptions(intl)}
                                        placeholder={intl.formatMessage({ id: "reviermanager.filter.all", defaultMessage: "Alle" })}
                                        label={intl.formatMessage({ id: "reviermanager.camera.activity.filter.message", defaultMessage: "Type" })}
                                    />
                                </StyledFilter>
                            </Grid>
                        </TableFilter>
                    </TableFilterFinalForm>
                    <TableQuery api={api} loading={loading} error={error}>
                        {tableData && (
                            <Table
                                exportApis={[exportApi]}
                                {...tableData}
                                sortApi={sortApi}
                                rowName={intl.formatMessage(
                                    {
                                        id: "reviermanager.camera.activity.table",
                                        defaultMessage: "{count, plural, one {Eintrag} other {Einträge}}",
                                    },
                                    { count: tableData.totalCount },
                                )}
                                renderTableRow={(props) => <TableRow {...props} />}
                                columns={[
                                    {
                                        name: "message",
                                        header: "",
                                        visible: { [VisibleType.Browser]: true, [VisibleType.Export]: false },
                                        render: ({ message }) => <Type message={message} />,
                                    },
                                    {
                                        name: "createdAt",
                                        sortable: true,
                                        header: intl.formatMessage({ id: "reviermanager.camera.activity.date", defaultMessage: "Datum" }),
                                        render: ({ createdAt }) => (
                                            <StyledTypography>
                                                <FormattedDateWidthDateTime value={createdAt} />
                                            </StyledTypography>
                                        ),
                                        renderExcel: ({ createdAt }) => formatDateTimeAsString(createdAt, intl),
                                    },
                                    {
                                        name: "executedBy",
                                        header: intl.formatMessage({ id: "reviermanager.camera.activity.executedBy", defaultMessage: "Absender" }),
                                        render: ({ executedBy }) => (
                                            <StyledChip
                                                label={
                                                    (executedBy && (executedBy === "App" ? executedBy : "Admin")) ||
                                                    intl.formatMessage({ id: "reviermanager.camera.activity.user.camera", defaultMessage: "Kamera" })
                                                }
                                            />
                                        ),
                                        renderExcel: ({ executedBy }) =>
                                            (executedBy && executedBy === "App" ? executedBy : "Admin") ||
                                            intl.formatMessage({ id: "reviermanager.camera.activity.user.camera", defaultMessage: "Kamera" }),
                                    },
                                    {
                                        name: "type",
                                        header: intl.formatMessage({ id: "reviermanager.camera.activity.type", defaultMessage: "Typ" }),
                                        visible: { [VisibleType.Browser]: false, [VisibleType.Export]: true },
                                        render: ({ message }) => generateType(message, intl),
                                    },
                                    {
                                        name: "content",
                                        header: intl.formatMessage({
                                            id: "reviermanager.camera.activity.content.command",
                                            defaultMessage: "Inhalt/Befehl",
                                        }),
                                        render: (row) => {
                                            let content = generateContent(intl, row);
                                            if (!content) {
                                                content = generateType(row.message, intl);
                                            }

                                            return (
                                                <>
                                                    <StyledContentTypography>{content}</StyledContentTypography>
                                                    <StyledCommandTypography>{row.send?.command}</StyledCommandTypography>
                                                </>
                                            );
                                        },

                                        renderExcel: (row) => `${generateContent(intl, row)} ${row.send?.command}`,
                                    },

                                    {
                                        name: "groupIdentifier",
                                        header: intl.formatMessage({ id: "reviermanager.camera.activity.content", defaultMessage: "Befehl-ID" }),
                                        render: ({ groupIdentifier }) => groupIdentifier,
                                        visible: { [VisibleType.Browser]: false, [VisibleType.Export]: true },
                                    },
                                    {
                                        name: "actions",
                                        visible: { [VisibleType.Browser]: true, [VisibleType.Export]: false },
                                        render: ({ groupIdentifier }) =>
                                            groupIdentifier && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setCurrentGroupId(groupIdentifier);
                                                        setDialogOpen(true);
                                                    }}
                                                >
                                                    <ArrowRight color="primary" />
                                                </IconButton>
                                            ),
                                    },
                                ]}
                            />
                        )}
                        {tableData && <CSVExportButton exportApi={exportApi} />}
                    </TableQuery>
                </ContentWrapper>
            </Grid>
            <Grid size={12}>
                <LastReceivedImageInformation cameraAdmin={cameraAdmin} />
            </Grid>
            <Grid size={12}>
                <ImageLogTable cameraId={cameraAdmin.id} isRefetching={loading} />
            </Grid>
        </>
    );
};
export default ActivityInformation;

interface Row {
    id: string;
    message: string;
}

const TableRow: FC<ITableRowProps<Row>> = ({ rowProps, ...rest }) => {
    return (
        <StyledTableBodyRow {...rowProps} $variant={rest.row.message}>
            <TableColumns {...rest} />
        </StyledTableBodyRow>
    );
};
